import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { AccountsPayableService, AccountsPayablePagedResult, AccountsPayableFilters } from '../../../services/accounts-payable.service';
import { SuppliersService } from '../../../services/suppliers.service';
import { CostCentersService } from '../../../services/cost-centers.service';
import { StatusService } from '../../../services/status.service';
import { ContractsService } from '../../../services/contracts.service';
import { CostCenter } from '../../../models/CostCenter';
import { AccountsPayable } from '../../../models/AccountsPayable';
import { Supplier } from '../../../models/Supplier';
import { Status } from '../../../models/Status';
import { Contract, B2bContract } from '../../../models/Contract';
import { TableAction } from '../../../shared/components/table/table.component';
import { canPayAccountsPayable } from '../../../utils/accounts-payable.utils';
import { AccountsPayableCreateMode } from '../../../models/accounts-payable-create.mode';

export interface DepositRefundContractRow {
  id: number;
  title: string;
  subtitle: string;
  depositAmount?: number;
  hint?: string;
}

@Component({
  selector: 'app-accounts-payable',
  templateUrl: './accounts-payable.component.html',
  styleUrls: ['./accounts-payable.component.css']
})
export class AccountsPayableComponent implements OnInit, OnDestroy {
  // Configuração da tabela
  tableTitle = 'Gestão de Contas a Pagar';
  showSearch = true;
  searchPlaceholder = 'Buscar contas a pagar...';
  clickable = true;

  // Colunas da tabela
  columns = [
    { titulo: 'Documento', label: 'documentNumber' },
    { titulo: 'Tipo', label: 'documentType' },
    { titulo: 'Fornecedor', label: 'supplierName' },
    { titulo: 'Valor a Pagar', label: 'amountToPay', pipe: 'currency' },
    { titulo: 'Valor Pago', label: 'amountPaid', pipe: 'currency' },
    { titulo: 'Vencimento', label: 'dueDate', pipe: 'date' },
    { titulo: 'Status', label: 'status', badge: true },
    { titulo: 'Categoria', label: 'category' }
  ];

  // Ações da tabela
  actions = [
    {
      label: 'Visualizar',
      icon: 'fas fa-eye',
      action: 'view',
      class: 'btn-view'
    },
    {
      label: 'Editar',
      icon: 'fas fa-pen-to-square',
      action: 'edit',
      class: 'btn-edit'
    },
    {
      label: 'Excluir',
      icon: 'fas fa-trash-can',
      action: 'delete',
      class: 'btn-delete'
    }
  ];

  // Dados
  accountsPayable: AccountsPayable[] = [];
  suppliers: Supplier[] = [];
  costCenters: CostCenter[] = [];
  selectedAccountsPayable: AccountsPayable | null = null;
  loading = false;
  error: string | null = null;

  showCreateModeModal = false;

  // Paginação
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;
  hasNextPage = false;
  hasPreviousPage = false;

  // Filtros
  filters: AccountsPayableFilters & { 
    selectedSupplierId: number | string | null;
    selectedStatus: string | null;
    selectedCategory: string | null;
    selectedCostCenterId: number | string | null;
    dueStartDate: string;
    dueEndDate: string;
    paymentStartDate: string;
    paymentEndDate: string;
    searchTerm: string;
  } = {
    selectedSupplierId: '',
    selectedStatus: '',
    selectedCategory: '',
    selectedCostCenterId: '',
    dueStartDate: '',
    dueEndDate: '',
    paymentStartDate: '',
    paymentEndDate: '',
    searchTerm: '',
    page: 1,
    pageSize: 10
  };
  loadingStatuses = false;
  
  // Timeout para debounce da busca
  private searchTimeout: any;

  showDepositRefundModal = false;
  depositRefundMode: 'b2c' | 'b2b' = 'b2c';
  depositRefundSearch = '';
  depositRefundResults: DepositRefundContractRow[] = [];
  depositRefundLoading = false;
  private depositRefundSearchTimeout: ReturnType<typeof setTimeout> | null = null;
  private b2bContractsCache: B2bContract[] = [];

  // Opções para filtros
  statusOptions: Status[] = [];

  categoryOptions = [
    'Fornecedores',
    'Serviços',
    'Manutenção',
    'Combustível',
    'Seguros',
    'Impostos',
    'Aluguel',
    'Utilidades',
    'Marketing',
    'Material de Escritório',
    'Equipamentos',
    'Treinamento',
    'Consultoria',
    'Outros'
  ];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private accountsPayableService: AccountsPayableService,
    private suppliersService: SuppliersService,
    private costCentersService: CostCentersService,
    private statusService: StatusService,
    private contractsService: ContractsService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.applyListPaginationFromQueryParams(this.route.snapshot.queryParamMap);
    this.route.queryParamMap.subscribe((params) => {
      const prevPage = this.filters.page;
      const prevSize = this.filters.pageSize;
      this.applyListPaginationFromQueryParams(params);
      if (params.get('page') != null || params.get('pageSize') != null) {
        if (this.filters.page !== prevPage || this.filters.pageSize !== prevSize) {
          this.loadAccountsPayable();
        }
      }
    });
    this.loadSuppliers();
    this.loadCostCenters();
    this.loadAccountsPayable();
    this.loadTotals();
    this.loadAccountsPayableStatuses();
  }

  private applyListPaginationFromQueryParams(
    params: { get: (name: string) => string | null }
  ): void {
    const pageRaw = params.get('page');
    if (pageRaw != null) {
      const page = Number(pageRaw);
      if (!Number.isNaN(page) && page > 0) {
        this.currentPage = page;
        this.filters.page = page;
      }
    }
    const pageSizeRaw = params.get('pageSize');
    if (pageSizeRaw != null) {
      const pageSize = Number(pageSizeRaw);
      if (!Number.isNaN(pageSize) && pageSize > 0) {
        this.pageSize = pageSize;
        this.filters.pageSize = pageSize;
      }
    }
  }

  loadSuppliers(): void {
    this.suppliersService.getAll().subscribe({
      next: (suppliers: Supplier[]) => {
        this.suppliers = suppliers || [];
      },
      error: (error: any) => {
        console.error('Erro ao carregar fornecedores:', error);
        this.suppliers = [];
      }
    });
  }

  loadCostCenters(): void {
    this.costCentersService.getActive().subscribe({
      next: (result) => {
        this.costCenters = result.items || [];
      },
      error: (error: any) => {
        console.error('Erro ao carregar centros de custo:', error);
        this.costCenters = [];
      }
    });
  }

  loadAccountsPayable(): void {
    this.loading = true;
    this.error = null;

    // Prepara os filtros removendo valores vazios
    const filters: AccountsPayableFilters = {
      page: this.filters.page || this.currentPage || 1,
      pageSize: this.filters.pageSize || this.pageSize || 10
    };
    
    if (this.filters.selectedSupplierId && this.filters.selectedSupplierId !== '' && Number(this.filters.selectedSupplierId) > 0) {
      filters.supplierId = Number(this.filters.selectedSupplierId);
    }
    if (this.filters.selectedStatus && this.filters.selectedStatus.trim()) {
      filters.status = this.filters.selectedStatus.trim();
    }
    if (this.filters.selectedCostCenterId && this.filters.selectedCostCenterId !== '' && Number(this.filters.selectedCostCenterId) > 0) {
      filters.costCenterId = Number(this.filters.selectedCostCenterId);
    }
    if (this.filters.selectedCategory && this.filters.selectedCategory.trim()) {
      filters.category = this.filters.selectedCategory.trim();
    }
    if (this.filters.dueStartDate && this.filters.dueStartDate.trim()) {
      filters.dueDateStart = this.filters.dueStartDate.trim();
    }
    if (this.filters.dueEndDate && this.filters.dueEndDate.trim()) {
      filters.dueDateEnd = this.filters.dueEndDate.trim();
    }
    if (this.filters.paymentStartDate && this.filters.paymentStartDate.trim()) {
      filters.paymentDateStart = this.filters.paymentStartDate.trim();
    }
    if (this.filters.paymentEndDate && this.filters.paymentEndDate.trim()) {
      filters.paymentDateEnd = this.filters.paymentEndDate.trim();
    }
    if (this.filters.searchTerm && this.filters.searchTerm.trim()) {
      filters.searchTerm = this.filters.searchTerm.trim();
    }

    this.accountsPayableService.getAllAccountsPayable(filters).subscribe({
      next: (result: AccountsPayablePagedResult) => {
        this.accountsPayable = this.decorateAccountsPayable(result.items || []);
        this.totalItems = result.totalItems;
        this.totalPages = result.totalPages;
        this.currentPage = result.page;
        this.pageSize = result.pageSize;
        this.filters.page = result.page;
        this.filters.pageSize = result.pageSize;
        this.hasNextPage = this.currentPage < this.totalPages;
        this.hasPreviousPage = this.currentPage > 1;
        this.loading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar contas a pagar:', error);
        this.error = `Erro ao carregar contas a pagar: ${error.message || 'Erro desconhecido'}`;
        this.loading = false;
        this.accountsPayable = [];
      }
    });
  }

  onSearchChange(searchTerm: string): void {
    // Limpar timeout anterior se o usuário continuar digitando
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    
    // Atualiza o termo de busca nos filtros
    this.filters.searchTerm = searchTerm || '';
    
    // Aguardar 600ms após parar de digitar antes de fazer a requisição
    this.searchTimeout = setTimeout(() => {
      this.filters.page = 1;
      this.currentPage = 1;
      this.loadAccountsPayable();
      this.searchTimeout = null;
    }, 600);
  }

  onSupplierFilterChange(supplierId: number | string | null): void {
    this.filters.selectedSupplierId = supplierId;
    this.applyFilters();
  }

  onStatusFilterChange(status: string | null): void {
    this.filters.selectedStatus = status;
    this.applyFilters();
  }

  onCategoryFilterChange(category: string | null): void {
    this.filters.selectedCategory = category;
    this.applyFilters();
  }

  onCostCenterFilterChange(costCenterId: number | string | null): void {
    this.filters.selectedCostCenterId = costCenterId;
    this.applyFilters();
  }

  onDueRangeChange(): void {
    this.applyFilters();
  }

  onPaymentRangeChange(): void {
    this.applyFilters();
  }

  applyFilters(): void {
    // Reset para primeira página ao aplicar filtros
    this.filters.page = 1;
    this.currentPage = 1;
    this.loadAccountsPayable();
  }

  clearFilters(): void {
    this.filters = {
      selectedSupplierId: '',
      selectedStatus: '',
      selectedCategory: '',
      selectedCostCenterId: '',
      dueStartDate: '',
      dueEndDate: '',
      paymentStartDate: '',
      paymentEndDate: '',
      searchTerm: '',
      page: 1,
      pageSize: 10
    };
    this.currentPage = 1;
    this.loadAccountsPayable();
  }
  
  ngOnDestroy(): void {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    if (this.depositRefundSearchTimeout) {
      clearTimeout(this.depositRefundSearchTimeout);
    }
  }

  retryLoad(): void {
    this.loadAccountsPayable();
  }

  onActionClick(actionData: {action: string, item: any}): void {
    const { action, item } = actionData;
    
    switch (action) {
      case 'view':
        this.viewAccountsPayable(item);
        break;
      case 'edit':
        this.editAccountsPayable(item);
        break;
      case 'pay':
        if (canPayAccountsPayable(item?.status)) {
          this.payAccountsPayable(item);
        }
        break;
      case 'delete':
        this.deleteAccountsPayable(item);
        break;
      default:
    }
  }

  viewAccountsPayable(accountsPayable: AccountsPayable): void {
    this.router.navigate(['/financial/accounts-payable', accountsPayable.id, 'view']);
  }

  editAccountsPayable(accountsPayable: AccountsPayable): void {
    this.router.navigate(['/financial/accounts-payable', accountsPayable.id, 'edit']);
  }

  payAccountsPayable(accountsPayable: AccountsPayable): void {
    if (!canPayAccountsPayable(accountsPayable.status)) {
      return;
    }
    this.router.navigate(['/financial/accounts-payable', accountsPayable.id, 'view'], {
      queryParams: { action: 'pay' }
    });
  }

  private decorateAccountsPayable(items: AccountsPayable[]): (AccountsPayable & { _actions?: TableAction[] })[] {
    return (items || []).map(item => ({
      ...item,
      _actions: this.buildRowActions(item)
    }));
  }

  private buildRowActions(ap: AccountsPayable): TableAction[] {
    const actions: TableAction[] = [
      { label: 'Visualizar', icon: 'fas fa-eye', action: 'view', class: 'btn-view', visible: true },
      { label: 'Editar', icon: 'fas fa-pen-to-square', action: 'edit', class: 'btn-edit', visible: true }
    ];
    if (canPayAccountsPayable(ap.status)) {
      actions.push({
        label: 'Pagar',
        icon: 'fas fa-credit-card',
        action: 'pay',
        class: 'btn-pay',
        visible: true
      });
    }
    actions.push({
      label: 'Excluir',
      icon: 'fas fa-trash-can',
      action: 'delete',
      class: 'btn-delete',
      visible: true
    });
    return actions;
  }

  deleteAccountsPayable(accountsPayable: AccountsPayable): void {
    const doc = accountsPayable.documentNumber || String(accountsPayable.id);
    this.confirmationService
      .confirmDelete(doc, 'a conta a pagar')
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.accountsPayableService.deleteAccountsPayable(accountsPayable.id).subscribe({
          next: () => this.loadAccountsPayable(),
          error: (error) => console.error('Erro ao excluir conta a pagar:', error)
        });
      });
  }

  newAccountsPayable(): void {
    this.showCreateModeModal = true;
  }

  closeCreateModeModal(): void {
    this.showCreateModeModal = false;
  }

  startNewAccountsPayable(mode: AccountsPayableCreateMode): void {
    this.showCreateModeModal = false;
    this.router.navigate(['/financial/accounts-payable/new'], { queryParams: { mode } });
  }

  openDepositRefundModal(): void {
    this.showDepositRefundModal = true;
    this.depositRefundMode = 'b2c';
    this.depositRefundSearch = '';
    this.loadDepositRefundContracts();
  }

  closeDepositRefundModal(): void {
    this.showDepositRefundModal = false;
    this.depositRefundResults = [];
    if (this.depositRefundSearchTimeout) {
      clearTimeout(this.depositRefundSearchTimeout);
      this.depositRefundSearchTimeout = null;
    }
  }

  setDepositRefundMode(mode: 'b2c' | 'b2b'): void {
    if (this.depositRefundMode === mode) return;
    this.depositRefundMode = mode;
    this.depositRefundSearch = '';
    this.loadDepositRefundContracts();
  }

  onDepositRefundSearchChange(): void {
    if (this.depositRefundSearchTimeout) {
      clearTimeout(this.depositRefundSearchTimeout);
    }
    this.depositRefundSearchTimeout = setTimeout(() => this.loadDepositRefundContracts(), 350);
  }

  private loadDepositRefundContracts(): void {
    if (!this.showDepositRefundModal) return;
    this.depositRefundLoading = true;
    if (this.depositRefundMode === 'b2b') {
      this.loadDepositRefundB2bContracts();
      return;
    }
    this.loadDepositRefundB2cContracts();
  }

  private loadDepositRefundB2cContracts(): void {
    const q = this.depositRefundSearch.trim();
    this.contractsService.searchContracts({ q: q || undefined }).subscribe({
      next: (contracts) => {
        this.depositRefundResults = (contracts || []).slice(0, 30).map((c) => this.mapB2cDepositRefundRow(c));
        this.depositRefundLoading = false;
      },
      error: () => {
        this.depositRefundResults = [];
        this.depositRefundLoading = false;
      }
    });
  }

  private loadDepositRefundB2bContracts(): void {
    const term = this.depositRefundSearch.trim().toLowerCase();
    const applyFilter = (list: B2bContract[]) => {
      const filtered = term
        ? list.filter((c) => {
            const hay = [
              c.contractNumber,
              c.contractName,
              c.companyName,
              c.cnpj
            ]
              .filter(Boolean)
              .join(' ')
              .toLowerCase();
            return hay.includes(term);
          })
        : list;
      this.depositRefundResults = filtered.slice(0, 30).map((c) => this.mapB2bDepositRefundRow(c));
      this.depositRefundLoading = false;
    };

    if (this.b2bContractsCache.length > 0) {
      applyFilter(this.b2bContractsCache);
      return;
    }

    this.contractsService.listB2bContracts(1, 100).subscribe({
      next: (res) => {
        this.b2bContractsCache = res.data || [];
        applyFilter(this.b2bContractsCache);
      },
      error: () => {
        this.depositRefundResults = [];
        this.depositRefundLoading = false;
      }
    });
  }

  private mapB2cDepositRefundRow(c: Contract): DepositRefundContractRow {
    const driverName =
      c.driver?.name ||
      (c as unknown as { driverName?: string }).driverName ||
      '—';
    return {
      id: c.id,
      title: `Contrato #${c.contractNumber || c.id}`,
      subtitle: `Motorista: ${driverName} · Status: ${c.status || '—'}`,
      depositAmount: Number(c.depositAmount) || undefined,
      hint: c.depositPaid === false ? 'Caução ainda não marcada como paga no contrato.' : undefined
    };
  }

  private mapB2bDepositRefundRow(c: B2bContract): DepositRefundContractRow {
    return {
      id: c.id,
      title: c.contractName || c.contractNumber || `Contrato ${c.id}`,
      subtitle: `${c.companyName || '—'} · ${c.contractNumber || ''} · ${c.status || ''}`,
      depositAmount: Number(c.depositAmount) || undefined
    };
  }

  goToDepositRefund(contractId: number): void {
    this.closeDepositRefundModal();
    const returnState = {
      accountsPayableReturn: {
        page: this.currentPage,
        pageSize: this.pageSize
      }
    };
    if (this.depositRefundMode === 'b2b') {
      this.router.navigate(['/b2b/contracts', contractId, 'deposit-refund'], { state: returnState });
    } else {
      this.router.navigate(['/contracts', contractId, 'deposit-refund'], { state: returnState });
    }
  }

  getSupplierName(supplierId?: number): string {
    if (!supplierId) return 'N/A';
    const supplier = this.suppliers.find(s => s.id === supplierId);
    return supplier ? supplier.companyName : 'N/A';
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'pendente':
        return 'status-pending';
      case 'pago':
        return 'status-paid';
      case 'cancelado':
        return 'status-cancelled';
      case 'vencido':
        return 'status-overdue';
      case 'parcialmente pago':
        return 'status-partial';
      default:
        return 'status-default';
    }
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  totalPendingAmount: number | null = null;
  totalOverdueAmount: number | null = null;

  loadAccountsPayableStatuses(): void {
    this.loadingStatuses = true;
    this.statusService.getByEntity('AccountsPayable', true)
      .subscribe({
        next: (statuses: Status[]) => {
          this.statusOptions = statuses;
          this.loadingStatuses = false;
        },
        error: (err) => {
          console.error('Erro ao carregar status de contas a pagar:', err);
          this.loadingStatuses = false;
          // Fallback para status básicos em caso de erro
          this.statusOptions = [
            { id: 1, name: 'Pendente', description: 'Conta pendente', entityType: 'AccountsPayable', isActive: true },
            { id: 2, name: 'Pago', description: 'Conta paga', entityType: 'AccountsPayable', isActive: true },
            { id: 3, name: 'Cancelado', description: 'Conta cancelada', entityType: 'AccountsPayable', isActive: true },
            { id: 4, name: 'Vencido', description: 'Conta vencida', entityType: 'AccountsPayable', isActive: true },
            { id: 5, name: 'Parcialmente Pago', description: 'Conta parcialmente paga', entityType: 'AccountsPayable', isActive: true }
          ] as Status[];
        }
      });
  }

  private loadTotals(): void {
    this.accountsPayableService.getTotalPendingAmount().subscribe({
      next: (res) => { this.totalPendingAmount = res?.totalPendingAmount ?? 0; },
      error: () => { this.totalPendingAmount = null; }
    });
    this.accountsPayableService.getTotalOverdueAmount().subscribe({
      next: (res) => { this.totalOverdueAmount = res?.totalOverdueAmount ?? 0; },
      error: () => { this.totalOverdueAmount = null; }
    });
  }

  formatDate(date: string | Date): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('pt-BR');
  }

  onPageChange(page: number): void {
    this.filters.page = page;
    this.currentPage = page;
    this.loadAccountsPayable();
  }

  onPageSizeChange(pageSize: number): void {
    this.filters.pageSize = pageSize;
    this.pageSize = pageSize;
    this.filters.page = 1;
    this.currentPage = 1;
    this.loadAccountsPayable();
  }
}
